import $ from 'jquery';
import APIV5 from '~/models/api';
import ClassDesigner from '~/scripts/services/model/class-designer';
import { NodeService as NodeServiceModel } from '~/scripts/services/model/node-service';
import { UserModel } from '~/scripts/services/model/user';
import Schema from '~/scripts/services/model/schema';
import { ConfirmDeleteModal } from '~/utils/modals';
import './admin-class-designer.scss';

export default class AdminClassDesignerController {
	#opening;

	/** @type {?File} */
	#temporaryFile;

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('angular').ILocationService} $location
	 * @param {import('angular').route.IRouteParamsService} $routeParams
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {NodeServiceModel} NodeService
	 * @param {UserModel} User
	 */
	constructor(
		$injector,
		$location,
		$routeParams,
		$scope,
		$timeout,
		$translate,
		$uibModal,
		NodeService,
		User,
	) {
		this.$injector = $injector;
		this.$location = $location;
		this.$routeParams = $routeParams;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$uibModal = $uibModal;
		this.NodeService = NodeService;
		this.User = User;

		this.active = 0;
		this.creating = false;

		/** @type {any[]} */
		this.schemaList = [];

		/** @type {?import('@knitiv/api-client-javascript').KNode} */
		this.searchKid = null;

		/** @type {ClassDesigner[] | any[]} */
		this.tabs = [
			{
				id:          'home',
				schema: {
					// @ts-ignore
					kid:  null,
					getName: () => this.$translate.instant('common.home'),
				},
			},
		];

		// -------------- Private vars --------------
		this.#opening = false;
		this.#temporaryFile = null;

		// -------------- Events --------------
		this.$scope.$on('jsplumb.instance.created', (/** @type {Event} */ event, /** @type {any} */ instance) => {
			if (this.active === 999 || this.active === null || this.active === 0) {
				this.active = this.tabs.length - 1;
			}
			if (this.active in this.tabs) {
				this.tabs[this.active].init(instance);
			}
		});

		this.$scope.$on('$locationChangeSuccess', async () => {
			const search = this.$location.search();
			if ('kid' in search) {
				await this.open(
					search.kid,
					'edit' in search,
					'create' in search,
					'openNode' in search ? search.openNode : false,
					'duplicate' in search,
				);
			}
		});

		// -------------- Main --------------
		if (this.$routeParams.kid) {
			(async () => this.open(
				this.$routeParams.kid,
				this.$routeParams.edit || false,
				this.$routeParams.create || false,
				this.$routeParams.openNode || false,
			))();
		}
	}

	/**
	 * @param {number} index
	 * @returns {Promise<any>}
	 */
	async closeSchema(index) {
		if (index > 0) {
			await this.tabs[index]?.close();
			this.active = this.active >= index && index - 1 in this.tabs ? index - 1 : 0;
			return this.$timeout(() => {
				this.tabs.splice(index, 1);
			});
		}
		return false;
	}

	/**
	 * @param {Event} event
	 * @param {Schema} schema
	 */
	async confirmDeleteSchema(event, schema) {
		const modal = new ConfirmDeleteModal(this.$injector);
		const answer = await modal.open(schema.name || '');

		if (answer) {
			try {
				await this.NodeService.delete(schema.kid);
				await this.homeListSchemas();
				if (this.active > 0) {
					await this.closeSchema(this.active);
				}
			}
			catch {
				if (event) {
					// @ts-ignore
					$(event.target).closest('.list-group-item').trigger('blur');
				}
			}
		}
	}

	/**
	 * @param {ClassDesigner} tab
	 */
	edit(tab) {
		tab.edit = !tab.edit;
		tab.jsp?.setDraggable($('.tab-pane:visible').find('.schema-editor .node'), tab.edit);
		this.$location.search({ edit: tab.edit || null, kid: tab.schema.kid });
	}

	async homeListSchemas() {
		/** @type {any} */
		const result = await APIV5.instance.mfSuggestion2(
			'K_NODE;CLASS_DESIGNER_SCHEMA',
			{ nocache: true },
			{ id: 'admin-class-designer.home-list-schemas' },
		);
		this.$timeout(() => {
			this.schemaList = result?.result || [];
		});
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} schemaId
	 * @param {boolean} edit
	 * @param {boolean} duplicate
	 * @returns {Promise<any>}
	 */
	async load(schemaId, edit, duplicate) {
		const newTab = new ClassDesigner(this.$injector);
		const tab = await newTab.load(schemaId, edit, duplicate);
		if (tab) {
			this.tabs.push(tab);
		}
	}

	newSchema() {
		this.creating = true;
		this.$timeout(() => {
			if (this.creating) {
				this.$location.search({ create: true, edit: true, kid: `K_NODE;${this.User.getRef()}` });
				this.creating = false;
			}
		});
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode | 'home' } schemaId
	 * @param {boolean} edit
	 * @param {boolean} create
	 * @param {any} openNode
	 * @param {boolean} [duplicate]
	 */
	async open(schemaId, edit, create, openNode, duplicate = false) {
		if (this.#opening || !('tabs' in this)) {
			return;
		}

		if (schemaId === 'home') {
			this.active = 0;
		}
		else {
			let isOpen = false;
			// search in tabs if one is opened
			for (const [k, tab] of this.tabs.entries()) {
				if (tab.schema?.kid === schemaId) {
					this.active = k;
					isOpen = true;
				}
			}

			try {
				if (!isOpen) {
					if (create) {
						/** @type {any} */
						const newTab = new ClassDesigner(this.$injector);
						await newTab.createTab(schemaId);
						this.tabs.push(newTab);

						this.$timeout(async () => {
							if (this.#temporaryFile) {
								this.tabs.at(-1).import(this.#temporaryFile);
								this.$timeout(() => {
									this.#temporaryFile = null;
								});
							}
							if (openNode) {
								await newTab.nodeOpen(openNode);
								this.searchKid = null;
							}
						});
					}
					else {
						await this.load(schemaId, edit, duplicate);
					}
					this.$timeout(() => {
						this.active = this.tabs.length - 1;
					});
				}
			}
			catch (error) {
				console.error(error);
				this.#opening = false;
			}
		}
	}

	/**
	 * @param {File[]} files
	 */
	import(files) {
		if (this.active) {
			this.tabs[this.active].import(files[0]);
		}
		else {
			this.#temporaryFile = files[0];
			this.newSchema();
		}
	}

	// eslint-disable-next-line class-methods-use-this
	openSchemaFromFile() {
		$('#input-file-hidden').find('input[type="file"]').trigger('click');
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode | 'home'} schemaId
	 * @param {boolean} edit
	 * @param {boolean} create
	 * @param {boolean} duplicate
	 */
	async updatePath(schemaId, edit, create, duplicate) {
		if (!schemaId) {
			return;
		}

		if (schemaId === 'home') {
			this.$location.search({
				create:    null,
				edit:      null,
				kid:       null,
				duplicate: null,
			});
			await this.homeListSchemas();
		}
		else {
			this.$location.search({
				create:    create || null,
				edit:      edit || null,
				kid:       schemaId,
				openNode:  this.searchKid || null,
				duplicate: duplicate || null,
			});
		}
	}
}
